import { useQueries, useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import type { DailyStats, UserPoints } from '@chore-project/shared';

export function useLeaderboard(householdId: string) {
  return useQuery<UserPoints[]>({
    queryKey: ['points', 'leaderboard', householdId],
    queryFn: async () => {
      const { data } = await apiClient.get<UserPoints[]>(`/households/${householdId}/points`);
      return data;
    },
    enabled: !!householdId,
  });
}

export function useDailyStats(householdId: string, date: string) {
  return useQuery<DailyStats[]>({
    queryKey: ['points', 'daily', householdId, date],
    queryFn: async () => {
      const { data } = await apiClient.get<DailyStats[]>(
        `/households/${householdId}/points/daily?date=${date}`,
      );
      return data;
    },
    enabled: !!householdId && !!date,
  });
}

/** Fetches daily stats for the current ISO week (Monday → today) in a single API call. */
export function useCurrentWeekDailyStats(householdId: string) {
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0] as string;
  // ISO week starts on Monday (day 1); Sunday is day 0 → treat as day 7
  const dayOfWeek = today.getUTCDay() === 0 ? 7 : today.getUTCDay();
  const monday = new Date(today);
  monday.setUTCDate(today.getUTCDate() - (dayOfWeek - 1));
  const mondayStr = monday.toISOString().split('T')[0] as string;

  const days: string[] = [];
  const cursor = new Date(monday);
  while (cursor.toISOString().split('T')[0]! <= todayStr) {
    days.push(cursor.toISOString().split('T')[0] as string);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  const { data = [], isLoading } = useQuery<DailyStats[]>({
    queryKey: ['points', 'range', householdId, mondayStr, todayStr],
    queryFn: async () => {
      const { data } = await apiClient.get<DailyStats[]>(
        `/households/${householdId}/points/range?from=${mondayStr}&to=${todayStr}`,
      );
      return data;
    },
    enabled: !!householdId,
    staleTime: 5 * 60 * 1000,
  });

  return { days, data, isLoading };
}

/**
 * Fetches the last 7 days of daily stats across all households and aggregates
 * dailyPoints + maxPoints per date per user by summing across households.
 */
export function useAllWeeklyDailyStats(householdIds: string[]) {
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - (6 - i));
    return d.toISOString().split('T')[0] as string;
  });

  const from = days[0]!;
  const to = days[6]!;

  const results = useQueries({
    queries: householdIds.map((id) => ({
      queryKey: ['points', 'range', id, from, to],
      queryFn: async () => {
        const { data } = await apiClient.get<DailyStats[]>(
          `/households/${id}/points/range?from=${from}&to=${to}`,
        );
        return data;
      },
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const isLoading = results.some((r) => r.isLoading);

  // Aggregate: sum dailyPoints and maxPoints per (userId, date) across all households
  const totals = new Map<string, { userId: string; date: string; dailyPoints: number; maxPoints: number }>();
  for (const result of results) {
    for (const stat of result.data ?? []) {
      const key = `${stat.userId}#${stat.date}`;
      const existing = totals.get(key);
      if (existing) {
        existing.dailyPoints += stat.dailyPoints;
        existing.maxPoints += stat.maxPoints;
      } else {
        totals.set(key, { userId: stat.userId, date: stat.date, dailyPoints: stat.dailyPoints, maxPoints: stat.maxPoints });
      }
    }
  }

  return { days, data: [...totals.values()], isLoading };
}
